import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Card, Typography, App, theme, Spin } from 'antd';
import { SelectBox, ButtonBox } from '@/components/common/controls';
import { LockOutlined, UserOutlined, BankOutlined } from '@ant-design/icons';
import { TextInput } from '@/components/common/inputs';
import { useAuthStore } from '@/modules/auth/authStore';
import { useAppStore } from '@/stores/appStore';
import { gradients, shadows } from '@/theme';
import type { AxiosError } from 'axios';
import type { LoginRequest, LoginResponse } from '@/types/api';
import api from '@/services/api';
import bookuLogo from '@/assets/booku-logo.png';

const { Title, Text } = Typography;

// Deteksi mode Desktop vs SaaS
const isDesktopMode = import.meta.env.VITE_MODE === 'Desktop';

interface CompanyListItem {
  code: string;
  name: string;
}

export default function LoginPage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const { token } = theme.useToken();
  // Gunakan App.useApp() untuk message agar bisa mengakses theme context
  const { message } = App.useApp();

  // Fetch companies untuk mode Desktop
  useEffect(() => {
    if (isDesktopMode) {
      const fetchCompanies = async () => {
        setLoadingCompanies(true);
        try {
          const response = await api.get<CompanyListItem[]>('/auth/companies');
          setCompanies(response.data);

          // Jika hanya ada 1 perusahaan, auto-select
          if (response.data.length === 1) {
            form.setFieldValue('tenantCode', response.data[0].code);
          }
        } catch (error) {
          console.error('Failed to fetch companies:', error);
          message.error('Gagal memuat daftar perusahaan');
        } finally {
          setLoadingCompanies(false);
        }
      };
      fetchCompanies();
    }
  }, [form]);

  const onFinish = async (values: LoginRequest) => {
    setLoading(true);
    try {
      const response = await api.post<LoginResponse>('/auth/login', values);
      const { accessToken, refreshToken, user, tenant, tahunBukuAktif, tahunBukuKemarin, jenisTahunBuku, statusBuku, karantinaCoa } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      login(user, tenant);

      // Simpan data tahun buku ke appStore
      useAppStore.getState().setAppInfo({
        TahunBukuAktif: tahunBukuAktif,
        TahunBukuKemarin: tahunBukuKemarin,
        JenisTahunBuku: jenisTahunBuku as 'NORMAL' | 'LAMPAU',
        StatusBuku: statusBuku as 'OPEN' | 'CLOSED',
        KarantinaCoa: karantinaCoa === true,
      });

      if (karantinaCoa) {
        // Tenant masih COA format lama. Diarahkan langsung ke satu-satunya menu
        // yang terbuka — menu lain akan menampilkan angka nol yang menyesatkan.
        message.warning(
          'Perusahaan ini masih memakai COA format lama. Hanya menu Migrasi COA 9 Digit yang tersedia sampai migrasi selesai.',
          10,
        );
        navigate('/migrasi-coa');
      } else {
        message.success('Login berhasil!');
        navigate('/');
      }
    } catch (error) {
      // Sebagian penolakan BUKAN soal kredensial — mis. tenant yang COA-nya masih
      // format lama (409). Menampilkan "periksa username dan password" untuk kasus
      // itu justru mengirim user memburu masalah yang tidak ada, jadi pesan dari
      // server dipakai apa adanya bila tersedia.
      const respons = (error as AxiosError<{ message?: string }>)?.response;
      const pesanServer = respons?.data?.message;

      message.error(
        pesanServer && respons?.status !== 401
          ? pesanServer
          : 'Login gagal. Periksa kembali username dan password.',
        pesanServer && respons?.status !== 401 ? 8 : undefined,
      );
    } finally {
      setLoading(false);
    }
  };

  // Render field perusahaan berdasarkan mode
  const renderCompanyField = () => {
    if (!isDesktopMode) {
      // Mode SaaS: Textbox kode perusahaan
      return (
        <Form.Item
          name="tenantCode"
          label="Kode Perusahaan"
          rules={[{ required: true, message: 'Masukkan kode perusahaan' }]}
        >
          <TextInput context="auth" prefix={<BankOutlined />} placeholder="Masukkan kode perusahaan" />
        </Form.Item>
      );
    }

    // Mode Desktop
    if (loadingCompanies) {
      return (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <Spin size="small" />
          <Text type="secondary" style={{ marginLeft: 8 }}>Memuat daftar perusahaan...</Text>
        </div>
      );
    }

    // Jika hanya 1 perusahaan, tidak perlu tampilkan dropdown
    if (companies.length === 1) {
      return (
        <Form.Item name="tenantCode" hidden>
          <TextInput context="auth" />
        </Form.Item>
      );
    }

    // Jika > 1 perusahaan, tampilkan dropdown
    if (companies.length > 1) {
      return (
        <Form.Item
          name="tenantCode"
          label="Perusahaan"
          rules={[{ required: true, message: 'Pilih perusahaan' }]}
        >
          <SelectBox
            context="auth"
            placeholder="Pilih perusahaan"
            suffixIcon={<BankOutlined />}
            options={companies.map(c => ({
              value: c.code,
              label: c.name,
            }))}
          />
        </Form.Item>
      );
    }

    // Tidak ada perusahaan
    return (
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <Text type="danger">Tidak ada perusahaan terdaftar</Text>
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: gradients.primaryGradient,
        padding: 24,
      }}
    >
      <Card
        style={{
          width: 440,
          boxShadow: shadows.lg,
          borderLeft: `4px solid ${token.colorPrimary}`,
        }}
        styles={{ body: { padding: 32 } }}
      >
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
          <img
            src={bookuLogo}
            alt="Logo Booku"
            style={{ height: 48, width: 48, flexShrink: 0 }}
          />
          <div>
            <Title level={3} style={{ color: token.colorPrimary, marginBottom: 4, lineHeight: 1.2 }}>
              Booku
            </Title>
            <Text type="secondary" style={{ fontSize: 14 }}>Sistem Akuntansi Terpadu</Text>
          </div>
        </div>

        <div
          style={{
            borderTop: '1px solid #f0f0f0',
            marginBottom: 24,
          }}
        />

        <Form
          form={form}
          name="login"
          className="booku-auth-form"
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}
        >
          {renderCompanyField()}

          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Masukkan username' }]}
          >
            <TextInput context="auth" prefix={<UserOutlined />} />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Masukkan password' }]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <ButtonBox
              context="auth"
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              // Desktop: nonaktif SELAMA daftar perusahaan dimuat — field `tenantCode`
              // (beserta aturan `required`-nya) belum dirender saat spinner tampil,
              // sehingga Masuk yang ditekan di jeda itu mengirim login tanpa
              // perusahaan ⇒ 400 "missing tenantCode" (temuan simulasi 21-09-2026).
              disabled={isDesktopMode && (loadingCompanies || companies.length === 0)}
            >
              Masuk
            </ButtonBox>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
